import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEOHead from '../components/common/SEOHead';
import ContactForm from '../components/common/ContactForm';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

const ContactPage = () => {
  const { t } = useLanguage();

  const contactMethods = [
    {
      icon: Phone,
      title: t('contact.info.phone'),
      content: (
        <div className="space-y-2">
          <p>
            <span className="font-semibold">Marcelo Junior:</span>{' '}
            <a href="tel:+447708773091" className="hover:text-gold-main transition-colors">
              +44 7708 773091
            </a>
          </p>
          <p>
            <span className="font-semibold">Marcelo Botelho:</span>{' '}
            <a href="tel:+447842485139" className="hover:text-gold-main transition-colors">
              +44 7842485139
            </a>
          </p>
        </div>
      ),
    },
    {
      icon: Mail,
      title: t('contact.info.email'),
      content: (
        <a 
          href="mailto:eurofortaleza2025@gmail.com" 
          className="hover:text-gold-main transition-colors"
          data-testid="contact-email-link"
        >
          eurofortaleza2025@gmail.com
        </a>
      ),
    },
    {
      icon: MapPin,
      title: t('contact.info.address'),
      content: (
        <address className="not-italic">
          20 Elm Road<br />
          Thornton Heath<br />
          Croydon, London<br />
          CR78RH<br />
          United Kingdom
        </address>
      ),
    },
    {
      icon: MessageCircle,
      title: t('contact.info.whatsapp'),
      content: (
        <a 
          href="https://wa.me/447842485139" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center space-x-2 text-green-600 hover:text-green-700 transition-colors font-semibold"
          data-testid="whatsapp-link"
        >
          <span>+44 7842485139</span>
          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Click to Chat</span>
        </a>
      ),
    },
    {
      icon: Clock,
      title: t('contact.info.hours'),
      content: (
        <div>
          <p className="font-semibold">{t('footer.hours')}</p>
          <p>{t('footer.hoursTime')}</p>
          <p className="mt-2 text-slate-500">{t('footer.sunday')}</p>
        </div>
      ),
    },
  ];

  return (
    <>
      <SEOHead 
        title={t('meta.contact.title')}
        description={t('meta.contact.description')}
      />

      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-16 bg-navy-deep"
        data-testid="contact-hero"
      >
        <div className="container-main">
          <div className="max-w-3xl">
            <motion.span 
              className="text-gold-main text-sm font-semibold uppercase tracking-widest block mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {t('contact.hero.label')}
            </motion.span>
            <motion.h1 
              className="font-alatsi text-5xl md:text-6xl text-white leading-tight mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="contact-title"
            >
              {t('contact.hero.title')}
            </motion.h1>
            <motion.p 
              className="text-xl text-gray-300 leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {t('contact.hero.subtitle')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="section-padding bg-white" data-testid="contact-info-section">
        <div className="container-main">
          <motion.div 
            className="mb-12"
            {...fadeUp}
          >
            <h2 className="font-alatsi text-4xl text-navy-deep">
              {t('contact.info.title')}
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {contactMethods.map((method, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="bg-slate-50 p-8 border-l-4 border-gold-main"
                data-testid={`contact-method-${index}`}
              >
                <div className="flex items-center space-x-4 mb-4">
                  <method.icon className="w-6 h-6 text-gold-main" strokeWidth={1.5} />
                  <h3 className="font-alatsi text-lg text-navy-deep">{method.title}</h3>
                </div>
                <div className="text-slate-600 text-sm leading-relaxed">
                  {method.content}
                </div>
              </motion.div>
            ))}

            {/* Instagram Card */}
            <motion.div
              variants={staggerItem}
              className="bg-slate-50 p-8 border-l-4 border-gold-main"
              data-testid="contact-instagram"
            >
              <div className="flex items-center space-x-4 mb-4">
                <svg className="w-6 h-6 text-gold-main" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                <h3 className="font-alatsi text-lg text-navy-deep">{t('contact.info.instagram')}</h3>
              </div>
              <a 
                href="https://www.instagram.com/eurofortaleza/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gold-main hover:text-navy-deep transition-colors font-semibold"
                data-testid="instagram-link"
              >
                @eurofortaleza
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding bg-slate-50" data-testid="contact-form-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <motion.div 
              className="text-center mb-12"
              {...fadeUp}
            >
              <h2 className="font-alatsi text-4xl text-navy-deep mb-4">
                {t('form.title')}
              </h2>
              <p className="text-slate-600">
                {t('form.subtitle')}
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 md:p-12 shadow-sm"
              {...fadeUp}
            >
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
